/**
 * The marketplace sort options, in the order the menu offers them.
 *
 * The set is deliberately narrower than the columns the API can order by. Offering every field in
 * both directions produced options nobody wants - the attorney who has drafted the fewest
 * applications - and hid the fact that most sorts only rank one kind of result. Each option here
 * says who it ranks, so choosing one is not a guess about where the firms went.
 */

/** Sort values the API accepts. Ordering matches the wire contract: `Field asc|desc`. */
export const MarketplaceSorts = {
  Relevance: 'relevance',
  NameAsc: 'Name asc',
  NameDesc: 'Name desc',
  ExperienceAsc: 'Experience asc',
  ExperienceDesc: 'Experience desc',
  AllowanceRateDesc: 'AllowanceRate desc',
  AvailabilityDesc: 'Availability desc',
  BillingRateAsc: 'BillingRate asc',

  // Superseded in the menu but still accepted, so links and bookmarks that carry them keep
  // working and stay correctly labelled. Experience covers people as well as firms; allowance
  // rate measures outcomes rather than raw drafting volume.
  BillingRateDesc: 'BillingRate desc',
  ApplicationsDraftedAsc: 'ApplicationsDrafted asc',
  ApplicationsDraftedDesc: 'ApplicationsDrafted desc',
  YearEstablishedAsc: 'YearEstablished asc',
  YearEstablishedDesc: 'YearEstablished desc',
} as const;

export type MarketplaceSortValue = (typeof MarketplaceSorts)[keyof typeof MarketplaceSorts];

export interface MarketplaceSortOption {
  value: MarketplaceSortValue;
  label: string;
  /** Which kinds of result this option actually ranks; the rest sort after them. */
  appliesTo: string;
}

export interface MarketplaceSortGroup {
  key: string;
  options: MarketplaceSortOption[];
}

const Everyone = 'Attorneys, firms and teams';
const AttorneysAndFirms = 'Attorneys and firms';
const AttorneysOnly = 'Attorneys';

export const marketplaceSortGroups: MarketplaceSortGroup[] = [
  {
    key: 'match',
    options: [
      { value: MarketplaceSorts.Relevance, label: 'Best match', appliesTo: Everyone },
    ],
  },
  {
    key: 'name',
    options: [
      { value: MarketplaceSorts.NameAsc, label: 'Name: A to Z', appliesTo: Everyone },
      { value: MarketplaceSorts.NameDesc, label: 'Name: Z to A', appliesTo: Everyone },
    ],
  },
  {
    key: 'experience',
    options: [
      {
        value: MarketplaceSorts.ExperienceAsc,
        label: 'Most experienced',
        appliesTo: AttorneysAndFirms,
      },
      {
        value: MarketplaceSorts.ExperienceDesc,
        label: 'Newest to practice',
        appliesTo: AttorneysAndFirms,
      },
    ],
  },
  {
    key: 'record',
    options: [
      {
        value: MarketplaceSorts.AllowanceRateDesc,
        label: 'Allowance rate: high to low',
        appliesTo: AttorneysOnly,
      },
      {
        value: MarketplaceSorts.AvailabilityDesc,
        label: 'Available now',
        appliesTo: AttorneysOnly,
      },
      {
        value: MarketplaceSorts.BillingRateAsc,
        label: 'Billing rate: low to high',
        appliesTo: AttorneysOnly,
      },
    ],
  },
];

const supersededLabels: Record<string, string> = {
  [MarketplaceSorts.BillingRateDesc]: 'Billing rate: high to low',
  [MarketplaceSorts.ApplicationsDraftedAsc]: 'Applications drafted: low to high',
  [MarketplaceSorts.ApplicationsDraftedDesc]: 'Applications drafted: high to low',
  [MarketplaceSorts.YearEstablishedAsc]: 'Year established: oldest first',
  [MarketplaceSorts.YearEstablishedDesc]: 'Year established: newest first',
};

const labelsByValue: Record<string, string> = {
  ...Object.fromEntries(
    marketplaceSortGroups.flatMap((group) => group.options.map((o) => [o.value, o.label])),
  ),
  ...supersededLabels,
};

const supportedSorts = new Set<string>(Object.values(MarketplaceSorts));

/** Falls back to best match for anything the API would reject. */
export function normalizeMarketplaceSort(
  sortBy: string | null | undefined,
): MarketplaceSortValue {
  return sortBy && supportedSorts.has(sortBy)
    ? (sortBy as MarketplaceSortValue)
    : MarketplaceSorts.Relevance;
}

export function marketplaceSortLabel(sortBy: string | null | undefined): string {
  return labelsByValue[normalizeMarketplaceSort(sortBy)];
}

/**
 * Whether a sort replaces the ranking an assisted search produced.
 *
 * An assisted search ranks its own results, and the API applies any explicit sort on top of that
 * ranking - discarding it. A sort chosen for an earlier search would otherwise silently outrank
 * every later AI search, which is why choosing AI search returns to best match.
 */
export function overridesAssistedRanking(sortBy: string | null | undefined): boolean {
  return normalizeMarketplaceSort(sortBy) !== MarketplaceSorts.Relevance;
}
